import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, MapPin, Search, Check, Loader2, LocateFixed, Edit3, MousePointer } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Google Places powered address autocomplete with Google Maps display
// Includes manual mode for addresses not found via autocomplete (Squeegee-like)
const AddressPickerModal = ({ 
  isOpen,
  onClose, 
  onSave,
  initialAddress = '',
  initialLat = null,
  initialLng = null
}) => {
  const [address, setAddress] = useState(initialAddress);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState({
    lat: initialLat || 50.8225,
    lng: initialLng || -0.1372
  });
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Manual mode state - for when autocomplete doesn't find the address
  const [isManualMode, setIsManualMode] = useState(false);
  const [manualInstructions, setManualInstructions] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const isManualModeRef = useRef(isManualMode);
  
  // Keep ref in sync with state
  useEffect(() => {
    isManualModeRef.current = isManualMode;
  }, [isManualMode]);

  // Load Google Maps API
  useEffect(() => {
    if (!isOpen) return;

    if (window.google && window.google.maps) {
      setGoogleLoaded(true);
      return;
    }

    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.maps) {
          setGoogleLoaded(true);
          clearInterval(checkGoogle);
        }
      }, 100);
      return () => clearInterval(checkGoogle);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps API');
    document.head.appendChild(script);
  }, [isOpen]);

  // Initialize Google Places services and Map
  useEffect(() => {
    if (!googleLoaded || !window.google || !mapRef.current || !isOpen) return;
    if (mapInstanceRef.current) return;

    // Initialize Places services
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    const dummyDiv = document.createElement('div');
    placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);

    // Initialize Google Map
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
      zoom: 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] }
      ]
    });

    // Create draggable marker
    const marker = new window.google.maps.Marker({
      position: { lat: selectedLocation.lat, lng: selectedLocation.lng },
      map: map,
      draggable: true,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#10b981',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      }
    });

    // Handle marker drag
    marker.addListener('dragend', (e) => {
      setSelectedLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });

    // Handle map click to move marker
    map.addListener('click', (e) => {
      const newPos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      marker.setPosition(newPos);
      setSelectedLocation(newPos);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    if (initialLat && initialLng) {
      map.setCenter({ lat: initialLat, lng: initialLng });
      map.setZoom(17);
      marker.setPosition({ lat: initialLat, lng: initialLng });
    }

    return () => {
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [googleLoaded, isOpen]);

  // Search for addresses using Google Places Autocomplete
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    if (!autocompleteServiceRef.current) {
      console.warn('Google Places not loaded yet');
      return;
    }

    setIsSearching(true);
    
    try {
      const request = {
        input: query,
        componentRestrictions: { country: 'gb' },
        sessionToken: sessionTokenRef.current,
        types: ['address']
      };

      autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
        setIsSearching(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.map(prediction => ({
            place_id: prediction.place_id,
            display_name: prediction.description,
            main_text: prediction.structured_formatting?.main_text || prediction.description,
            secondary_text: prediction.structured_formatting?.secondary_text || ''
          })));
          setShowSuggestions(true);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setSuggestions([]);
        } else {
          console.error('Places autocomplete error:', status);
          setSuggestions([]);
        }
      });
    } catch (err) {
      console.error('Search error:', err);
      setSuggestions([]);
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddresses(address);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [address, searchAddresses]);

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    if (!placesServiceRef.current) {
      console.warn('PlacesService not ready');
      return;
    }

    setIsSearching(true);

    placesServiceRef.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ['formatted_address', 'geometry', 'address_components'],
        sessionToken: sessionTokenRef.current
      },
      (place, status) => {
        setIsSearching(false);

        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          // Door-number preservation (May 2026 — Denis Horan bug).
          // Google's `getDetails` sometimes resolves a "11 Smallfield Cl..."
          // prediction to a ROUTE-level place_id (the whole street) rather
          // than a PREMISE-level one (the specific number). When that
          // happens, `formatted_address` comes back as the street name
          // only — silently dropping the door number the user typed and
          // selected. Detect that case by checking whether the
          // address_components include a `street_number` AND whether the
          // prediction's main_text starts with a number that the
          // formatted_address has lost. If so, keep what the user picked.
          const hasStreetNumberComponent = (place.address_components || []).some(
            (c) => Array.isArray(c.types) && c.types.includes('street_number')
          );
          const mainText = suggestion.main_text || '';
          const leadingNumberMatch = mainText.match(/^(\d+[a-z]?)\b/i);
          const formatted = place.formatted_address || '';
          const formattedKeepsNumber = leadingNumberMatch
            ? new RegExp(`\\b${leadingNumberMatch[1]}\\b`, 'i').test(formatted)
            : true;

          let finalAddress;
          if (leadingNumberMatch && (!hasStreetNumberComponent || !formattedKeepsNumber)) {
            // We had a door number in the user's selection but Google's
            // formatted_address has dropped it. Reconstruct by prepending
            // the door number to the formatted_address, or fall back to
            // the prediction's full description (which is always what the
            // user saw on screen and tapped on).
            if (formatted && !formattedKeepsNumber) {
              finalAddress = `${leadingNumberMatch[1]} ${formatted}`;
            } else {
              finalAddress = suggestion.display_name;
            }
          } else {
            finalAddress = formatted || suggestion.display_name;
          }

          setAddress(finalAddress);
          setSelectedLocation({ lat, lng });
          setShowSuggestions(false);
          setSuggestions([]);

          // Update map
          if (mapInstanceRef.current && markerRef.current) {
            mapInstanceRef.current.setCenter({ lat, lng });
            mapInstanceRef.current.setZoom(17);
            markerRef.current.setPosition({ lat, lng });
          }

          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        } else {
          console.error('Place details error:', status);
        }
      }
    );
  };

  // Get current location
  const handleGetCurrentLocation = (autoFillAddress = true) => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedLocation({ lat: latitude, lng: longitude });

        if (mapInstanceRef.current && markerRef.current) {
          mapInstanceRef.current.setCenter({ lat: latitude, lng: longitude });
          mapInstanceRef.current.setZoom(17);
          markerRef.current.setPosition({ lat: latitude, lng: longitude });
        }

        // Reverse geocode using Google (only if autoFillAddress is true)
        if (autoFillAddress && window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              setAddress(results[0].formatted_address);
            }
          });
        }
        
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsGettingLocation(false);
        alert('Unable to get your location. Please enter the address manually.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Enter manual mode - for when address can't be found via autocomplete
  const handleEnterManualMode = () => {
    setIsManualMode(true);
    setSuggestions([]);
    setShowSuggestions(false);
    setManualInstructions('Tap on the map to place the pin at the correct location');
    
    // Start at user's current location for easier manual pin placement
    handleGetCurrentLocation(false);
  };

  // Exit manual mode
  const handleExitManualMode = () => {
    setIsManualMode(false);
    setManualInstructions('');
  };

  // Handle save
  const handleSave = () => {
    onSave({
      address: address,
      latitude: selectedLocation.lat,
      longitude: selectedLocation.lng
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" 
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden modal-shell-h flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin size={24} />
              <h3 className="text-lg font-semibold">Set Location</h3>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Manual Mode Banner */}
          {isManualMode && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <MousePointer size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">Manual Pin Placement Mode</p>
                  <p className="text-xs text-amber-600 mt-1">{manualInstructions}</p>
                </div>
                <button
                  onClick={handleExitManualMode}
                  className="text-xs text-amber-700 hover:text-amber-900 font-medium"
                >
                  Exit
                </button>
              </div>
            </div>
          )}

          {/* Address Entry - supports both autocomplete and manual text */}
          <div className="relative">
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-2">
              {isManualMode ? <Edit3 size={16} className="text-zinc-400" /> : <Search size={16} className="text-zinc-400" />}
              {isManualMode ? 'Enter Address Manually' : 'Search Address'}
            </label>
            <div className="relative">
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onFocus={() => !isManualMode && suggestions.length > 0 && setShowSuggestions(true)}
                placeholder={isManualMode ? "Type the full address here..." : "Start typing a UK address or postcode..."}
                className="w-full px-4 py-3 pr-10 rounded-xl border border-zinc-300 text-zinc-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none"
                data-testid="address-search-input"
              />
              {isSearching && !isManualMode && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-zinc-400" />
              )}
            </div>

            {/* Suggestions Dropdown - only show if not in manual mode */}
            {!isManualMode && showSuggestions && suggestions.length > 0 && (
              <div 
                className="absolute z-50 w-full mt-1 bg-white rounded-xl border border-zinc-200 shadow-xl overflow-hidden"
                data-testid="address-suggestions"
              >
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={suggestion.place_id || idx}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 transition-colors border-b border-zinc-100 last:border-0"
                    data-testid={`address-suggestion-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <MapPin size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">{suggestion.main_text}</p>
                        {suggestion.secondary_text && (
                          <p className="text-xs text-zinc-500 truncate">{suggestion.secondary_text}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
                <div className="px-4 py-2 bg-zinc-50 border-t border-zinc-200 flex items-center gap-2">
                  <span className="text-xs text-zinc-400">Powered by</span>
                  <svg height="12" viewBox="0 0 116 38" className="h-3">
                    <path fill="#4285F4" d="M18.9 16.55c0-1.13-.1-2.22-.29-3.27H9.7v6.18h5.18c-.22 1.2-.9 2.22-1.92 2.9v2.42h3.1c1.82-1.68 2.87-4.14 2.87-7.23z"/>
                    <path fill="#34A853" d="M9.7 24.9c2.6 0 4.78-.86 6.37-2.34l-3.1-2.42c-.86.58-1.96.92-3.27.92-2.52 0-4.65-1.7-5.41-3.99H1.1v2.5c1.58 3.14 4.83 5.33 8.6 5.33z"/>
                    <path fill="#FBBC05" d="M4.29 17.07c-.19-.58-.3-1.2-.3-1.83s.11-1.25.3-1.83v-2.5H1.1c-.65 1.3-1.02 2.76-1.02 4.33s.37 3.03 1.02 4.33l3.19-2.5z"/>
                    <path fill="#EA4335" d="M9.7 8.42c1.42 0 2.7.49 3.7 1.45l2.77-2.77C14.47 5.54 12.3 4.58 9.7 4.58c-3.77 0-7.02 2.19-8.6 5.33l3.19 2.5c.76-2.29 2.89-3.99 5.41-3.99z"/>
                    <text x="30" y="22" fill="#5F6368" fontSize="14" fontFamily="Arial, sans-serif">Google</text>
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleGetCurrentLocation(true)}
              disabled={isGettingLocation}
              className="flex items-center gap-2 px-4 py-2.5 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors disabled:opacity-50"
              data-testid="use-current-location-btn"
            >
              {isGettingLocation ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <LocateFixed size={18} />
              )}
              <span className="text-sm font-medium">Use My Location</span>
            </button>
            
            {/* Manual Mode Toggle */}
            {!isManualMode ? (
              <button
                onClick={handleEnterManualMode}
                className="flex items-center gap-2 px-4 py-2.5 text-amber-600 hover:bg-amber-50 rounded-xl transition-colors"
                data-testid="manual-mode-btn"
              >
                <MousePointer size={18} />
                <span className="text-sm font-medium">Can&apos;t find address?</span>
              </button>
            ) : null}
          </div>

          {/* Map Container - Google Maps */}
          <div className="relative">
            <div 
              ref={mapRef} 
              className={`w-full h-64 rounded-xl border overflow-hidden bg-zinc-100 ${isManualMode ? 'border-amber-300 ring-2 ring-amber-200' : 'border-zinc-200'}`}
              style={{ minHeight: '250px' }}
              data-testid="address-picker-map"
            >
              {!googleLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              )}
            </div>
            <p className={`text-xs mt-2 text-center ${isManualMode ? 'text-amber-600 font-medium' : 'text-zinc-500'}`}>
              {isManualMode 
                ? 'Tap anywhere on the map to place the pin at the exact location'
                : 'Drag the pin to adjust the exact location'
              }
            </p>
          </div>

          {/* Coordinates Display */}
          <div className="flex items-center gap-4 p-3 bg-zinc-50 rounded-xl">
            <div className="flex-1">
              <p className="text-xs text-zinc-500">Latitude</p>
              <p className="text-sm font-mono text-zinc-700">{selectedLocation.lat.toFixed(6)}</p>
            </div>
            <div className="flex-1">
              <p className="text-xs text-zinc-500">Longitude</p>
              <p className="text-sm font-mono text-zinc-700">{selectedLocation.lng.toFixed(6)}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-200 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50 transition-colors"
            data-testid="address-picker-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-3 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
            data-testid="address-picker-save-btn"
          >
            <Check size={18} />
            Save Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressPickerModal;
